import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { isNetworkError } from '@xver/libs/connectionNotifier';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Endpoints } from '../../../../../apis';

type QueryError = { status?: number; message?: string } | null | undefined;

export function useBaremetalQuery(baremetalId: string | undefined) {
    const { t } = useTranslation('bareMetal');
    const navigate = useNavigate();
    const hasShownError = useRef(false);
    const hasRedirected = useRef(false);

    const query = useQuery({
        queryKey: ['api.v1.baremetals.get', baremetalId],
        queryFn: async () => Endpoints.v1.Baremetals.getBaremetal(baremetalId),
        enabled: !!baremetalId,
        retry: (failureCount, err) => {
            const status = (err as QueryError)?.status;
            return (status === undefined || status >= 500) && failureCount < 2;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        refetchInterval: 5000,
    });

    const { data, isPending, error, refetch } = query;
    const errStatus = (error as QueryError)?.status;
    // Without cached data, every poll resets the query to pending and clears `error`;
    // keep the last one so the page doesn't flash a loader between polls.
    const [lastError, setLastError] = useState<unknown>(null);

    useEffect(() => {
        if (error) setLastError(error);
        else if (data) setLastError(null);
    }, [error, data]);

    useEffect(() => {
        if (errStatus === 404 && !hasRedirected.current) {
            hasRedirected.current = true;
            notifications.show({
                title: t('notFound.title', 'Server not found'),
                message: t(
                    'notFound.message',
                    'This bare-metal server no longer exists. Redirecting to the server list.'
                ),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
            navigate('/bare-metal-compute', { replace: true });
        }
    }, [errStatus, navigate, t]);

    useEffect(() => {
        if (
            error &&
            errStatus !== 404 &&
            errStatus !== 400 &&
            !isNetworkError(error) &&
            !hasShownError.current
        ) {
            hasShownError.current = true;
            notifications.show({
                title: t('queryFailed.title', 'Failed to query'),
                message: t(
                    'queryFailed.baremetal',
                    'Failed to query bare-metal data. Please try again later.'
                ),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (query.isSuccess) {
            hasShownError.current = false;
        }
    }, [error, errStatus, query.isSuccess, t]);

    return { data, isPending, error: error ?? lastError, refetch };
}
